import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  ArrowLeft, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  XCircle,
  User,
  Calendar
} from 'lucide-react';
import { Feedback } from '../types';
import { hotelNameToSlug } from '../utils/format';
import { Button } from '../components/ui';
import { useUser } from '../contexts/UserContext';
import { subscribeToFeedback } from '../firebase/firestore';
import { formatDate } from '../utils/format';
import { cn } from '../utils/cn';
import { apiService } from '../services/api';

const priorityConfig = {
  low: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  medium: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  high: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  critical: { color: 'bg-red-100 text-red-800', icon: XCircle },
};

const statusConfig = {
  open: { color: 'bg-blue-100 text-blue-800', label: 'Open' },
  'in-progress': { color: 'bg-yellow-100 text-yellow-800', label: 'In Progress' },
  resolved: { color: 'bg-green-100 text-green-800', label: 'Resolved' },
  closed: { color: 'bg-gray-100 text-gray-800', label: 'Closed' },
};

const categoryConfig = {
  'bug-report': { color: 'bg-red-100 text-red-800', label: 'Bug Report' },
  'feature-request': { color: 'bg-blue-100 text-blue-800', label: 'Feature Request' },
  'ui-ux-issue': { color: 'bg-purple-100 text-purple-800', label: 'UI/UX Issue' },
  'general-feedback': { color: 'bg-gray-100 text-gray-800', label: 'General Feedback' },
};

export function Notifications() {
  const navigate = useNavigate();
  const { isAdmin } = useUser();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('unread');

  // Subscribe to all feedback updates
  useEffect(() => {
    const unsubscribe = subscribeToFeedback((feedbacks) => {
      setFeedbacks(feedbacks);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter feedbacks based on current filter
  const filteredFeedbacks = feedbacks.filter(feedback => {
    if (filter === 'unread') {
      return !feedback.isReadByAdmin;
    }
    return true;
  });

  const unreadCount = feedbacks.filter(f => !f.isReadByAdmin).length;

  const handleMarkAsRead = async (feedbackId: string) => {
    try {
      await apiService.updateFeedback(feedbackId, { isReadByAdmin: true });
    } catch (error) {
      console.error('Error marking feedback as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadFeedbacks = feedbacks.filter(f => !f.isReadByAdmin);
      const updatePromises = unreadFeedbacks.map(feedback => 
        apiService.updateFeedback(feedback.id, { isReadByAdmin: true })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking all feedback as read:', error);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger-600 mb-4">Access denied. Only admins can view notifications.</p>
          <Button onClick={() => navigate('/')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 pt-8 pb-8">
        {/* Header */}
        <div className="bg-white/20 backdrop-blur-2xl rounded-3xl border border-gray-200 shadow-lg mb-8 overflow-hidden">
          <div className="p-8 border-b border-white/20">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="space-y-2">
                <h1 className="text-4xl font-display font-bold text-gray-900">Notifications</h1>
                <p className="text-lg text-gray-600 font-text">
                  Feedback and system notifications
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Back Button */}
                <Button
                  variant="secondary"
                  onClick={() => navigate('/')}
                  className="group bg-white/95 backdrop-blur-xl border border-white/30 text-gray-700 hover:bg-white hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 rounded-2xl px-4 py-2 font-medium shadow-lg"
                >
                  <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
                  Back to Dashboard
                </Button>
                
                {/* Mark All as Read Button */}
                {unreadCount > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleMarkAllAsRead}
                    className="bg-white/40 backdrop-blur-xl border-gray-200 hover:bg-white/60"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark All as Read
                  </Button>
                )}
                
                {/* Notification Counter */}
                <div className="bg-white/30 backdrop-blur-xl px-6 py-3 rounded-2xl border border-white/20">
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Bell className="w-5 h-5" />
                    <span className="font-text font-semibold">
                      {unreadCount} unread
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Filter Tabs */}
          <div className="p-8">
            <div className="flex space-x-4">
              <button
                onClick={() => setFilter('unread')}
                className={cn(
                  'px-4 py-2 rounded-xl font-medium transition-all duration-200',
                  filter === 'unread'
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-400/25'
                    : 'bg-white/50 backdrop-blur-xl text-gray-700 hover:bg-white/70'
                )}
              >
                Unread ({unreadCount})
              </button>
              <button
                onClick={() => setFilter('all')}
                className={cn(
                  'px-4 py-2 rounded-xl font-medium transition-all duration-200',
                  filter === 'all'
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-400/25'
                    : 'bg-white/50 backdrop-blur-xl text-gray-700 hover:bg-white/70'
                )}
              >
                All ({feedbacks.length})
              </button>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white/20 backdrop-blur-2xl rounded-3xl border border-gray-200 shadow-lg overflow-hidden">
          <div className="p-8">
            {filteredFeedbacks.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <Bell className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-display font-bold text-gray-700 mb-3">
                  {filter === 'unread' ? 'No Unread Notifications' : 'No Notifications'}
                </h3>
                <p className="text-lg text-gray-500 font-text max-w-md mx-auto leading-relaxed">
                  {filter === 'unread' 
                    ? 'You\'re all caught up! No unread notifications.'
                    : 'No notifications have been created yet.'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredFeedbacks.map((feedback) => {
                  const PriorityIcon = priorityConfig[feedback.priority].icon;
                  
                  return (
                    <div 
                      key={feedback.id} 
                      onClick={() => navigate(`/hotel/${hotelNameToSlug(feedback.hotelName)}`)}
                      className={cn(
                        'bg-white/30 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer',
                        !feedback.isReadByAdmin && 'ring-2 ring-primary-400/30'
                      )}
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {!feedback.isReadByAdmin && (
                                <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                              )}
                              <h3 className="text-lg font-semibold text-gray-900">{feedback.title}</h3>
                            </div>
                            
                            <div className="flex items-center gap-2 mb-3">
                              <span className={cn(
                                'px-2 py-1 text-xs font-medium rounded-full',
                                categoryConfig[feedback.category].color
                              )}>
                                {categoryConfig[feedback.category].label}
                              </span>
                              <span className={cn(
                                'px-2 py-1 text-xs font-medium rounded-full',
                                priorityConfig[feedback.priority].color
                              )}>
                                <PriorityIcon className="w-3 h-3 inline mr-1" />
                                {feedback.priority.charAt(0).toUpperCase() + feedback.priority.slice(1)}
                              </span>
                              <span className={cn(
                                'px-2 py-1 text-xs font-medium rounded-full',
                                statusConfig[feedback.status].color
                              )}>
                                {statusConfig[feedback.status].label}
                              </span>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            {!feedback.isReadByAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsRead(feedback.id);
                                }}
                                className="text-blue-500 hover:text-blue-700"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-gray-700 text-sm leading-relaxed mb-4">{feedback.description}</p>

                        {/* Meta Information */}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              <span>{feedback.submittedByName}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>{formatDate(feedback.createdAt)}</span>
                            </div>
                          </div>
                          
                          {/* Click to view feedback */}
                          <div className="flex items-center gap-1 text-blue-600 hover:text-blue-700 cursor-pointer">
                            <span className="text-xs font-medium">Click to view</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
